import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

// Enum pour les noms d'API (correspondant au backend)
export enum ApiName {
  APRIL = 'april',
  CARDIF = 'cardif',
  UTWIN = 'utwin',
  NEOLIANE = 'NEOLIANE',
  AFIESCA = 'afiesca',
  ZENIOO = 'zenioo',
  RINGOVER = 'ringover'
}

export interface ApiCredentialCreate {
  user_id: number;
  api_name: ApiName;
  credential_key: string;
  credential_value: string;
}

export interface ApiCredentialUpdate {
  api_name?: ApiName;
  credential_key?: string;
  credential_value?: string;
}

export interface ApiCredentialResponse {
  id: number;
  user_id: number;
  api_name: ApiName;
  credential_key: string;
  credential_value: string;
  user?: {
    id: number;
    username: string;
    email: string;
  }; // Ajout des données utilisateur
}

export interface ApiCredentialInDB {
  id: number;
  user_id: number;
  api_name: ApiName;
  credential_key: string;
  credential_value: string;
  user?: {
    id: number;
    username: string;
    email: string;
  }; // Ajout des données utilisateur
}

@Injectable({
  providedIn: 'root'
})
export class ApiCredentialsService {

  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/api-credentials`;

  constructor(private http: HttpClient) { }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Créer de nouvelles credentials API (Admin seulement)
   */
  createApiCredential(credential: ApiCredentialCreate): Observable<ApiCredentialResponse> {
    const headers = this.getAuthHeaders();
    console.log('Creating credential:', credential); // Debug
    return this.http.post<ApiCredentialResponse>(this.apiUrl, credential, { headers }).pipe(
      catchError(error => {
        console.error('Error creating API credential:', error);
        console.error('Error details:', error.error); // Debug détaillé
        return throwError(() => error);
      })
    );
  }

  /**
   * Récupérer toutes les credentials de tous les utilisateurs (Admin seulement)
   */
  getAllApiCredentials(): Observable<ApiCredentialResponse[]> {
    const headers = this.getAuthHeaders();
    return this.http.get<ApiCredentialResponse[]>(`${this.apiUrl}/`, { headers }).pipe(
      catchError(error => {
        console.error('Error getting all API credentials:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Récupérer toutes les credentials d'un utilisateur par son ID (Admin seulement)
   */
  getCredentialsByUserId(userId: number): Observable<ApiCredentialResponse[]> {
    const headers = this.getAuthHeaders();
    return this.http.get<ApiCredentialResponse[]>(`${this.apiUrl}/user/${userId}`, { headers }).pipe(
      catchError(error => {
        console.error('Error getting credentials by user ID:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Récupérer une credential complète avec la valeur non masquée (Admin seulement)
   */
  getFullCredential(credentialId: number): Observable<ApiCredentialInDB> {
    const headers = this.getAuthHeaders();
    return this.http.get<ApiCredentialInDB>(`${this.apiUrl}/${credentialId}/full`, { headers }).pipe(
      catchError(error => {
        console.error('Error getting full credential:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Récupérer toutes les credentials pour une API spécifique (Admin seulement)
   * CORRECTION: Route changée de /{api_name} à /api/{api_name}
   */
  getCredentialsByApi(apiName: ApiName): Observable<ApiCredentialResponse[]> {
    const headers = this.getAuthHeaders();
    return this.http.get<ApiCredentialResponse[]>(`${this.apiUrl}/api/${apiName}`, { headers }).pipe(
      catchError(error => {
        console.error('Error getting credentials by API:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Mettre à jour une credential (Admin seulement)
   */
  updateApiCredential(credentialId: number, credentialUpdate: ApiCredentialUpdate): Observable<ApiCredentialResponse> {
    const headers = this.getAuthHeaders();
    return this.http.put<ApiCredentialResponse>(`${this.apiUrl}/${credentialId}`, credentialUpdate, { headers }).pipe(
      catchError(error => {
        console.error('Error updating API credential:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Supprimer une credential (Admin seulement)
   */
  deleteApiCredential(credentialId: number): Observable<void> {
    const headers = this.getAuthHeaders();
    return this.http.delete<void>(`${this.apiUrl}/${credentialId}`, { headers }).pipe(
      catchError(error => {
        console.error('Error deleting API credential:', error);
        return throwError(() => error);
      })
    );
  }


getCommissionOptions(partner: string): Observable<string[]> {
  const headers = this.getAuthHeaders();
  return this.http.get<string[]>(
    `${environment.apiUrl}${environment.apiEndpoint}/comparaison/commission_options/${partner}`,
    { headers }
  );
}

getCommissionSettings(): Observable<any> {
  const headers = this.getAuthHeaders();
  return this.http.get<any>(
    `${environment.apiUrl}${environment.apiEndpoint}/comparaison/commission_settings`,
    { headers }
  );
}

updateCommission(partner: string, code: string): Observable<any> {
  const headers = this.getAuthHeaders();
  return this.http.put(
    `${environment.apiUrl}${environment.apiEndpoint}/comparaison/commission_settings/${partner}`,
    { commission_code: code },
    { headers }
  );
} 

getPartnerLogoPath(partner: string): string {
  const logoMap: { [key: string]: string } = {
    'APRIL': 'assets/images/logos/april-logo.png',
    'CARDIF': 'assets/images/logos/cardif-logo.png',
    'UTWIN': 'assets/images/logos/utwin-logo.png',
    'NEOLIANE': 'assets/images/logos/neoliane-logo.png',
    'SWISSLIFE': 'assets/images/logos/swisslife-logo.png',
    'AFIESCA': 'assets/images/logos/afiesca-logo.png',
    'ZENIOO': 'assets/images/logos/zenioo-logo.png'
  };
  
  return logoMap[partner.toUpperCase()] || 'assets/images/logos/logo_assureurs.png';
}
}